import { defineComponent, computed } from "vue";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import type { IBranchResponse } from "../../../../resources-vue/vue-interfaces/i-branch";
import type { ITypeaheadOptions } from "../../../../resources-vue/vue-interfaces/i-typeahead";
import { IOrganization } from "../../../../resources-vue/vue-interfaces/i-calendar";
import { IPageCallbackResult } from "../../../../resources-vue/vue-interfaces/i-page-container";
import { IMultiSelectDropDownOption } from "../../../../resources-vue/vue-elements/MultiSelect/MultiSelect";
import { PAGE_FAIL_RESPONSE } from "../../../../resources/elements/pagination";
import {
    ApplicationEnum,
    ParentPermissionEnum,
    PermissionActionEnum,
    ApplicationNameEnum,
    FrequencyVariance
} from "../../../../enums/enums";
import { BranchesService } from "../../../../services/branches-service";
import { ToastrService } from "../../../../services/toastr.service";
import { AgencyService, IGetAgency, IGetOrganization } from "../../../../services/agency.service";
import { formatDateInput, formatDate } from "../../../../common/vue-helpers/modifiers/value-modifier";
import { PermissionManager } from "../../../../common/utilities/permission-manager";
import config from "../../../../common/config";
import { enumToMap } from "../../../../common/vue-helpers/modifiers/enum-modifiers";
import { IFrequencyWatchRequest } from "../../../../resources-vue/vue-interfaces/i-reports";
import { EnumsService } from "../../../../services/enums.service";
import { FrequencyStatuses } from "../../../../enums/FrequencyStatusesEnum";
import { TeamsService } from "../../../../services/teams.service";
import { IGetTeamsLeanQuery } from "../../../../resources-vue/vue-interfaces/i-team";
import { ProvidersService } from "../../../../services/providers-service";
import { IGetTagsByLocation } from "../../../../resources-vue/vue-interfaces/i-providers-service";

import PageLayout from "../../../../resources-vue/vue-custom-elements/PageLayout/PageLayout.vue";
import MultiSelectFilter from "../../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";
import MultiSelect from "../../../../resources-vue/vue-elements/MultiSelect/MultiSelect.vue";
import EmptyTextHandler from "../../../../resources-vue/vue-custom-elements/EmptyTextHandler/EmptyTextHandler.vue";
import ErrorMessageHandler from "../../../../resources-vue/vue-custom-elements/ErrorMessageHandler/ErrorMessageHandler.vue";
import Pagination from "../../../../resources-vue/vue-elements/Pagination/Pagination.vue";
import ListLayout from "../../../../resources-vue/vue-custom-elements/ListLayout/ListLayout.vue";
import SortableTh from "../../../../resources-vue/vue-custom-elements/SortableTh/SortableTh.vue";
import FrequencyWatchHosRow from "./FrequencyWatchHosRow/FrequencyWatchHosRow.vue";
import DateRangePicker from "../../../../resources-vue/vue-custom-elements/DateRangePicker/DateRangePicker.vue";

export default defineComponent({
    components: {
        PageLayout,
        MultiSelectFilter,
        MultiSelect,
        EmptyTextHandler,
        ErrorMessageHandler,
        Pagination,
        ListLayout,
        FrequencyWatchHosRow,
        SortableTh,
        DateRangePicker
    },
    props: {
        application: { typeof: ApplicationEnum, default: null },
        getFrequencyWatchData: { typeof: Function, default: null }
    },
    data() {
        return {
            columns: 14,
            pageSize: 10,
            isAddItemRequested: false,
            isLoading: false,
            isError: false,
            organizationId: "",
            sortBy: "patientLastName",
            sortOrder: "asc",
            visitTime: null,
            dialogRef: null,
            currentUser: null,
            freqyencyWatchHosPagination: null,
            page: null,
            dateFrom: moment().subtract(30, "days").format("MM/DD/YYYY") as string,
            dateTo: moment().add(30, "days").format("MM/DD/YYYY") as string,
            newDateRange: (moment().subtract(30, "days").format("MM/DD/YYYY") as string)
                .concat("-")
                .concat(moment().add(30, "days").format("MM/DD/YYYY") as string),
            customIds: [] as string[],
            selectedBranches: [] as string[],
            selectedTaskId: [] as string[],
            selectedAgencies: [] as string[],
            selectedAgenciesName: [] as string[],
            agencyList: [] as IGetAgency[],
            branches: [] as IBranchResponse[],
            branchList: [] as IBranchResponse[],
            selectedTasks: [] as ITypeaheadOptions[],
            selectedStatuses: [] as ITypeaheadOptions[],
            selectedTeams: [] as ITypeaheadOptions[],
            selectedTags: [] as ITypeaheadOptions[],
            organizationsList: [] as IOrganization[],
            organizations: [] as IGetOrganization[],
            agencyMultiSelectOptions: {} as IMultiSelectDropDownOption,
            branchMultiSelectOptions: {} as IMultiSelectDropDownOption,
            FrequencyVarianceEnums: FrequencyVariance,
            frequencyVariance: null as string,
            _branchService: null as BranchesService,
            _agencyService: null as AgencyService,
            _toastrService: null as ToastrService,
            _permissionManager: null as PermissionManager,
            _enumService: null as EnumsService,
            _teamService: null as TeamsService,
            _providerService: null as ProvidersService,
            frequencyDiscipline: null,
            agencyOptions: [] as any,
            selectedAgency: "" as string,
            formatDateInput,
            formatDate,
            enumToMap,
            moment
        };
    },
    computed: {
        hasData() {
            return this.page?.Items.length > 0;
        },
        loadingCount() {
            return this.page?.Items.length > 0 ? this.page.Items.length : config.pageSize;
        },
        applicationName() {
            let application = this.application;
            if (application === ApplicationEnum.AxxessHospiceFE) {
                application = ApplicationEnum.AxxessHospice;
            }
            return ApplicationNameEnum[application];
        }
    },

    watch: {
        frequencyVariance(newValue, oldValue) {
            if (newValue !== oldValue) {
                this.pageReset();
            }
        }
    },

    created() {
        this._branchService = Container.instance.get(BranchesService);
        this._agencyService = Container.instance.get(AgencyService);
        this._toastrService = Container.instance.get(ToastrService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._enumService = Container.instance.get(EnumsService);
        this._teamService = Container.instance.get(TeamsService);
        this._providerService = Container.instance.get(ProvidersService);
        this.freqyencyWatchHosPagination = this.$refs?.freqyencyWatchHosPagination as typeof Pagination;
        this.isLoading = true;
    },
    async beforeMount() {
        this.branches = await this._branchService.getAllBranches();
        this.branchList = this.branches.filter((branch) => branch.application == this.application);
    },
    async mounted() {
        this.organizations = await this._agencyService.getOrganizations();
        this.organizationId = window.localStorage.getItem(config.organizationId);
        this.agencyList = this.organizations.find(
            (organization: { id: string }) => organization.id === this.organizationId
        ).agencies;
        this.agencyList = this.agencyList.map((agency) => {
            let agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);
            if (application == 256) {
                application = ApplicationEnum.AxxessHospice;
            }
            return {
                ...agency,
                application: application
            };
        });
        await this.setAgencyMultiSelect();
        await this.setBranchMultiSelect();
        this.freqyencyWatchHosPagination = this.$refs?.freqyencyWatchHosPagination as typeof Pagination;
        this.pageReset();
    },
    methods: {
        changeDate(date: string, name: string) {
            if (name == "dateFrom") {
                this.dateFrom = date;
            }
            if (name == "dateTo") {
                this.dateTo = date;
            }
        },
        handleDateInputChange(e: any) {
            const name = e.target.name;
            const value = e.target.value;
            if (!value.length) {
                if (name === "dateFrom") {
                    this.changeDate(this.dateFrom, name);
                } else {
                    this.changeDate(this.dateTo, name);
                }
            } else {
                this.changeDate(value, name);
            }
        },
        async disciplineFetch(filter: string) {
            const frequencyDisciplineEnums = await this._enumService.getFrequencyDiscipline();
            this.frequencyDiscipline = frequencyDisciplineEnums.getJson();

            if (!this.frequencyDiscipline?.length) {
                return [];
            }

            const uniqueOptions = new Map();

            this.frequencyDiscipline.forEach((discipline: any) => {
                if (!uniqueOptions.has(discipline.name)) {
                    uniqueOptions.set(discipline.name, {
                        name: discipline.name,
                        value: discipline.value
                    });
                }
            });

            const result = Array.from(uniqueOptions.values()).filter(
                (x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
            return result;
        },

        async frequencyStatusesFetch(filter: string) {
            const frequencyStatusesEnums = Array.from(FrequencyStatuses.enumWithDescriptions.values());
            const uniqueOptions = new Map();
            frequencyStatusesEnums.forEach((status: any) => {
                if (!uniqueOptions.has(status.name)) {
                    uniqueOptions.set(status.name, {
                        name: status.name,
                        value: status.value
                    });
                }
            });
            const result = Array.from(uniqueOptions.values()).filter(
                (x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
            return result;
        },

        async fetchTags(filter: string) {
            const payload = {
                application: this.application,
                accountId: this.selectedAgency,
                locationIds: this.selectedBranches
            } as IGetTagsByLocation;
            const response = await this._providerService.getTagsByLocation(payload);
            const uniqueOptions = new Map();
            response.forEach((team: any) => {
                if (!uniqueOptions.has(team.name)) {
                    uniqueOptions.set(team.name, {
                        name: team.name,
                        value: team.name
                    });
                }
            });
            const result = Array.from(uniqueOptions.values()).filter(
                (x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
            return result;
        },

        selectedItemsUpdated(options: ITypeaheadOptions[]) {
            this.selectedTasks = options;
            this.pageReset();
        },

        selectedStatusesUpdated(options: ITypeaheadOptions[]) {
            this.selectedStatuses = options;
            this.pageReset();
        },

        selectedTagsUpdated(options: ITypeaheadOptions[]) {
            this.selectedTags = options;
            this.pageReset();
        },

        selectedTeamsUpdated(options: ITypeaheadOptions[]) {
            this.selectedTeams = options;
            this.pageReset();
        },

        async getTeamsList(filter: string) {
            const payload = {
                application: this.application,
                accountId: this.selectedAgency,
                locationIds: this.selectedBranches
            } as IGetTeamsLeanQuery;
            const response = await this._teamService.getTeamsLean(payload);
            const uniqueOptions = new Map();
            response.forEach((team: any) => {
                if (!uniqueOptions.has(team.name)) {
                    uniqueOptions.set(team.name, {
                        name: team.name,
                        value: team.id
                    });
                }
            });
            const result = Array.from(uniqueOptions.values()).filter(
                (x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
            return result;
        },
        async pageChangedCallback(pageNumber: number, pageSize: number): Promise<IPageCallbackResult> {
            let variance;
            if (this.frequencyVariance == "0") {
                variance = true;
            } else if (this.frequencyVariance == "1") {
                variance = false;
            } else {
                variance = null;
            }
            if (this.selectedBranches.length == 0) {
                this._toastrService.error({
                    title: `Error`,
                    message: `Select at least one agency and branch to proceed.`
                });
                return PAGE_FAIL_RESPONSE;
            }
            try {
                this.isLoading = true;
                this.isError = false;
                const disiplineIds = this.selectedTasks.map((option) => {
                    return option.value;
                });
                const statuses = this.selectedStatuses.map((option) => option.value);
                let payload: IFrequencyWatchRequest = {
                    Application: this.application,
                    AccountId: this.selectedAgency,
                    UserId: "",
                    LocationIds: this.selectedBranches,
                    StartDate: moment(this.dateFrom).toISOString(),
                    EndDate: moment(this.dateTo).toISOString(),
                    Page: pageNumber,
                    PageLength: pageSize,
                    TeamList: this.selectedTeams.map((team) => team.value),
                    LineOfService: 1,
                    IsUnder: variance,
                    Disciplines: disiplineIds,
                    Tags: this.selectedTags.map((tag) => tag.name),
                    SortBy: this.sortBy,
                    Order: this.sortOrder,
                    FrequencyStatuses: statuses
                };
                let response = await this.getFrequencyWatchData(payload, this.application);
                this.page = response.data;

                return {
                    totalPageCount: response.data.PageCount,
                    success: response.data.ItemCount > 0,
                    totalCount: response.data.ItemCount
                };
            } catch (e) {
                this.isError = true;
                return PAGE_FAIL_RESPONSE;
            } finally {
                this.isLoading = false;
            }
        },
        async setAgencyMultiSelect() {
            try {
                this.agencyMultiSelectOptions = {};
                this.selectedAgencies.length = 0;
                this.agencyOptions = [];
                if (!this.agencyList || this.agencyList?.length === 0) {
                    return;
                }
                let application = this.application == 256 ? ApplicationEnum.AxxessHospice : this.application;
                let allowedProducts = [application];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.scheduler,
                    PermissionActionEnum.canView
                );
                let schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        let currentapplication = item.application;
                        if (currentapplication === ApplicationEnum.AxxessHospiceFE) {
                            currentapplication = ApplicationEnum.AxxessHospice;
                        }
                        return allowedProducts.includes(currentapplication);
                    })
                    .map((x: any) => x.agencyId);
                let filterableProducts = this.agencyList.filter((agency) => {
                    let isAllowed = allowedProducts.includes(agency.application);
                    let agencyId = agency.id?.split("_")[0];
                    return isAllowed && schedulerAgencies.includes(agencyId);
                });
                filterableProducts?.forEach((agency, index) => {
                    let appName = ApplicationNameEnum[agency.application];

                    if (!this.agencyMultiSelectOptions[appName]) {
                        this.agencyMultiSelectOptions[appName] = [];
                    }

                    this.agencyMultiSelectOptions[appName].push({
                        displayText: agency.name,
                        value: agency.id?.split("_")[0]
                    });
                    this.agencyOptions.push({
                        name: agency.name,
                        value: agency.id?.split("_")[0]
                    });
                });
                this.selectedAgency = this.agencyOptions[0].value;
                // uncomment once BE accept multiple agencies
                // Object.values(this.agencyMultiSelectOptions).forEach((agency: any) => {
                //     let values = agency.map((item: any) => item.value);
                //     this.selectedAgencies.push(...values);
                // });
            } catch (e) {
                console.warn(e);
            }
        },
        async setBranchMultiSelect() {
            try {
                this.branchMultiSelectOptions = {};
                this.selectedBranches = [];
                if (!this.branchList || this.branchList?.length === 0) {
                    return;
                }
                let allowedProducts = [this.application];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.scheduler,
                    PermissionActionEnum.canView
                );

                // const selectedAgencies = this.selectedAgencies.map((agency: any) => agency.split("_")[0]);

                let schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        let application = item.application;
                        return allowedProducts.includes(application) && this.selectedAgency === item.agencyId;
                    })
                    .map((x: any) => x.agencyId);
                let filterableProducts = this.branchList.filter((branch) => {
                    let isAllowed = allowedProducts.includes(branch.application);
                    return isAllowed && schedulerAgencies.includes(branch.accountId);
                });
                filterableProducts?.forEach((branch) => {
                    let application = branch.application;
                    if (application === ApplicationEnum.AxxessHospiceFE) {
                        application = ApplicationEnum.AxxessHospice;
                    }
                    let appName = ApplicationNameEnum[application];

                    if (!this.branchMultiSelectOptions[appName]) {
                        this.branchMultiSelectOptions[appName] = [];
                    }

                    this.branchMultiSelectOptions[appName].push({
                        displayText: branch.name,
                        value: branch.id
                    });
                });
                Object.values(this.branchMultiSelectOptions).forEach((branch: any) => {
                    let values = branch.map((item: any) => item.value);
                    this.selectedBranches.push(...values);
                });
            } catch (e) {
                console.warn(e);
            }
        },
        updateBranchesbyAgency() {
            this.selectedAgenciesName = this.agencyList
                .filter((agency) => this.selectedAgency === agency.id)
                .map((agency) => agency.name);

            const filteredBranches = this.branches.filter(
                (branch) => branch.application == this.application && this.selectedAgency === branch.accountId
            );

            this.branchList = filteredBranches;
            this.setBranchMultiSelect();
            this.pageReset();
        },
        async pageReset() {
            this.isError = false;
            this.freqyencyWatchHosPagination?.reset();
        },

        clearFilters() {
            this.selectedTasks = [];
            this.frequencyVariance = null;
            this.selectedStatuses = [];
            this.selectedTeams = [];
            this.selectedTags = [];
            this.dateFrom = moment().subtract(30, "days").format("MM/DD/YYYY") as string;
            this.dateTo = moment().add(30, "days").format("MM/DD/YYYY") as string;
            this.newDateRange = this.dateFrom.concat("-").concat(this.dateTo);
            this.pageReset();
        },

        generate() {
            this.pageReset();
        },

        sortOrderUpdated(value: string) {
            if (value != this.sortOrder) {
                this.sortOrder = value;
                this.pageReset();
            }
        },
        sortByUpdated(value: string) {
            if (value != this.sortBy) {
                this.sortBy = value;
                this.pageReset();
            }
        },
        updatePageSizeChanged(value: number) {
            this.pageSize = value;
        },
        updateBranches(branches: any) {
            this.selectedBranches = [];
            this.selectedBranches.push(...branches);
            this.pageReset();
        },

        setnewTaskDateRange(value: any) {
            this.dateFrom = String(value.split("-")[0]).trim();
            this.dateTo = String(value.split("-")[1]).trim();
            this.newDateRange = value;
        }
    }
});
